import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { AddressAutocomplete } from '../../components/ui/AddressAutocomplete'
import {
  type AccessAddress,
  type AddressSearchResponse,
  type UnitAddress,
  formatEnhed,
} from '../../components/ui/address'
import { apiFetch } from '../../lib/api-client'

interface Company {
  id: string
  name: string
  cvr: string
}

const CONSUMPTION_TYPES = [
  { key: 'drift', label: 'Drift', desc: 'Standard forbrugstype for drift' },
  { key: 'el', label: 'El', desc: 'Standard forbrugstype for el' },
  { key: 'faellesudgifter', label: 'Fællesudgifter', desc: 'Standard forbrugstype for fællesudgifter' },
  { key: 'koeling', label: 'Køling', desc: 'Standard forbrugstype for køling' },
  { key: 'ovrig_forbrug', label: 'Øvrig forbrug', desc: 'Standard forbrugstype for øvrig forbrug' },
  { key: 'vand', label: 'Vand', desc: 'Standard forbrugstype for vand' },
  { key: 'vandafledning', label: 'Vandafledning', desc: 'Standard forbrugstype for vandafledning' },
  { key: 'varme', label: 'Varme', desc: 'Standard forbrugstype for varme' },
]

interface FormState {
  company_id: string
  address: string
  name: string
  commissioning_date: string
  serial_label: string
  consumption_types: Set<string>
}

interface UnitState {
  hit: UnitAddress
  selected: boolean
}

const sectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e8ecf4',
  borderRadius: 10,
  marginBottom: 20,
}

const sectionHeaderStyle: React.CSSProperties = {
  padding: '18px 24px 14px',
  borderBottom: '1px solid #e8ecf4',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}

const fieldRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '200px 1fr',
  alignItems: 'flex-start',
  padding: '14px 24px',
  borderBottom: '1px solid #f1f5f9',
  gap: 16,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#475569',
  paddingTop: 10,
  fontWeight: 500,
}

const fieldHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#94a3b8',
  marginTop: 4,
  lineHeight: 1.5,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  borderRadius: 7,
  border: '1px solid #e8ecf4',
  fontSize: 14,
  color: '#0f172a',
  background: '#ffffff',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}

const inputFocusStyle: React.CSSProperties = {
  border: '1px solid #4f46e5',
  boxShadow: '0 0 0 3px rgba(79,70,229,0.12)',
}

const inputReadonlyStyle: React.CSSProperties = {
  background: '#f8fafc',
  color: '#64748b',
  border: '1px solid #e8ecf4',
}

function TextInput({
  value, onChange, placeholder, readOnly, error,
}: {
  value: string
  onChange?: (v: string) => void
  placeholder?: string
  readOnly?: boolean
  error?: string
}) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <input
        type="text"
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={e => onChange?.(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...inputStyle,
          ...(readOnly ? inputReadonlyStyle : {}),
          ...(focused && !readOnly ? inputFocusStyle : {}),
          ...(error ? { border: '1px solid #dc2626' } : {}),
        }}
      />
      {error && <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{error}</p>}
    </div>
  )
}

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        border: 'none',
        cursor: 'pointer',
        background: on ? '#4f46e5' : '#cbd5e1',
        position: 'relative',
        transition: 'background 0.15s',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: on ? 21 : 3,
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.15s',
      }} />
    </button>
  )
}

export function PropertyCreatePage() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [nextSerial, setNextSerial] = useState<string>('01')
  const [form, setForm] = useState<FormState>({
    company_id: '',
    address: '',
    name: '',
    commissioning_date: '',
    serial_label: '01',
    consumption_types: new Set<string>(),
  })
  const [selectedHit, setSelectedHit] = useState<AccessAddress | null>(null)
  const [units, setUnits] = useState<UnitState[]>([])
  const [unitsLoading, setUnitsLoading] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    apiFetch<Company[]>('/api/v1/companies')
      .then((cs) => {
        setCompanies(cs)
        if (cs.length === 1) setForm(f => ({ ...f, company_id: cs[0].id }))
      })
      .catch(() => {})

    // Fetch next serial from existing properties count
    apiFetch<{ items: { serial_number: number | null }[]; total: number }>('/api/v1/properties')
      .then((r) => {
        const max = r.items.reduce((m, p) => Math.max(m, p.serial_number ?? 0), 0)
        const next = String(max + 1).padStart(2, '0')
        setNextSerial(next)
        setForm(f => ({ ...f, serial_label: next }))
      })
      .catch(() => {})
  }, [])

  const handleAddressHit = useCallback(async (hit: AccessAddress) => {
    setSelectedHit(hit)
    setForm(f => ({
      ...f,
      name: f.name || `${hit.vejnavn} ${hit.husnr}`,
    }))

    // Abort previous fetch
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setUnitsLoading(true)
    setUnits([])
    try {
      if (!hit.kommunekode || !hit.vejkode) return
      const qs = new URLSearchParams({
        kommunekode: hit.kommunekode,
        vejkode: hit.vejkode,
        husnr: hit.husnr,
        postnr: hit.postnr,
        vejnavn: hit.vejnavn,
      })
      const res = await apiFetch<AddressSearchResponse<UnitAddress>>(
        `/api/v1/properties/addresses/units?${qs}`,
        { signal: ctrl.signal }
      )
      setUnits(res.items.map(u => ({ hit: u, selected: true })))
    } catch {
      // aborted or failed — leave empty
    } finally {
      setUnitsLoading(false)
    }
  }, [])

  const toggleUnit = (idx: number) => {
    setUnits(prev => prev.map((u, i) => i === idx ? { ...u, selected: !u.selected } : u))
  }

  const toggleAll = (val: boolean) => {
    setUnits(prev => prev.map(u => ({ ...u, selected: val })))
  }

  const toggleConsumption = (key: string) => {
    setForm(f => {
      const next = new Set(f.consumption_types)
      if (next.has(key)) { next.delete(key) } else { next.add(key) }
      return { ...f, consumption_types: next }
    })
  }

  const validate = () => {
    const next: Record<string, string> = {}
    if (!form.company_id) next.company_id = 'Vælg en virksomhed'
    if (!form.address.trim()) next.address = 'Adresse er påkrævet'
    if (!form.name.trim()) next.name = 'Navn er påkrævet'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    setServerError(null)

    const selectedUnits = units
      .filter(u => u.selected)
      .map(u => ({ unit_no: formatEnhed(u.hit), dawa_address_id: u.hit.id }))

    try {
      const created = await apiFetch<{ id: string }>('/api/v1/properties', {
        method: 'POST',
        body: JSON.stringify({
          company_id: form.company_id,
          address: form.address.trim(),
          name: form.name.trim(),
          commissioning_date: form.commissioning_date || null,
          consumption_types: Array.from(form.consumption_types),
          postnr: selectedHit?.postnr ?? null,
          postnrnavn: selectedHit?.postnrnavn ?? null,
          dawa_access_id: selectedHit?.id ?? null,
          units: selectedUnits,
        }),
      })
      navigate(`/ejendomme/${created.id}`)
    } catch {
      setServerError('Kunne ikke oprette ejendommen. Prøv igen.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppLayout>
      <form onSubmit={handleSubmit}>
        {/* Page header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}>
          <div>
            <button
              type="button"
              onClick={() => navigate('/ejendomme')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, color: '#64748b', padding: 0, marginBottom: 6,
              }}
            >
              ← Tilbage til ejendomme
            </button>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>
              Opret ejendom
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {unitsLoading && (
              <span style={{ fontSize: 12, color: '#94a3b8' }}>Henter lejemål…</span>
            )}
            <Button type="button" variant="secondary" onClick={() => navigate('/ejendomme')}>
              Annuller
            </Button>
            <Button type="submit" isLoading={submitting} disabled={unitsLoading}>
              Opret ejendom
            </Button>
          </div>
        </div>

        {serverError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 8, padding: '12px 16px', marginBottom: 16,
            fontSize: 13, color: '#dc2626',
          }}>
            {serverError}
          </div>
        )}

        {/* EJENDOMSOPLYSNINGER */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>Ejendomsoplysninger</div>

          {/* Company selector */}
          <div style={fieldRowStyle}>
            <div>
              <div style={fieldLabelStyle}>Virksomhed<span style={{ color: '#dc2626' }}> *</span></div>
            </div>
            <div>
              {companies.length === 0 ? (
                <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                  Ingen virksomheder fundet.{' '}
                  <a href="/virksomheder" style={{ color: '#4f46e5' }}>Opret en virksomhed først.</a>
                </p>
              ) : (
                <select
                  value={form.company_id}
                  onChange={e => setForm({ ...form, company_id: e.target.value })}
                  style={{
                    ...inputStyle,
                    ...(errors.company_id ? { border: '1px solid #dc2626' } : {}),
                  }}
                >
                  <option value="">Vælg virksomhed</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.cvr})</option>
                  ))}
                </select>
              )}
              {errors.company_id && (
                <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{errors.company_id}</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div style={fieldRowStyle}>
            <div>
              <div style={fieldLabelStyle}>Ejendommens adgangsadresse<span style={{ color: '#dc2626' }}> *</span></div>
              <div style={fieldHintStyle}>
                Boligflow skal bruge adressen for at lokalisere ejendommen.
              </div>
            </div>
            <div>
              <AddressAutocomplete
                value={form.address}
                onChange={v => setForm(f => ({ ...f, address: v }))}
                onSelectHit={handleAddressHit}
                placeholder="Indtast adresse på ejendommen"
                error={errors.address}
              />
            </div>
          </div>

          {/* Name */}
          <div style={fieldRowStyle}>
            <div>
              <div style={fieldLabelStyle}>Ejendommens navn<span style={{ color: '#dc2626' }}> *</span></div>
              <div style={fieldHintStyle}>Valgfrit navn på ejendommen som vil blive vist i Boligflow.</div>
            </div>
            <div>
              <TextInput
                value={form.name}
                onChange={v => setForm(f => ({ ...f, name: v }))}
                placeholder="Indtast navn på ejendommen"
                error={errors.name}
              />
            </div>
          </div>

          {/* Commissioning date */}
          <div style={fieldRowStyle}>
            <div>
              <div style={fieldLabelStyle}>Oprindelig ibrugtagningsdato</div>
              <div style={fieldHintStyle}>
                Ændring af denne dato vil justere startdatoen for automiseringen af tomgangsleje.
              </div>
            </div>
            <div>
              <input
                type="date"
                value={form.commissioning_date}
                onChange={e => setForm(f => ({ ...f, commissioning_date: e.target.value }))}
                style={{ ...inputStyle, color: form.commissioning_date ? '#0f172a' : '#94a3b8' }}
              />
            </div>
          </div>

          {/* Serial number */}
          <div style={{ ...fieldRowStyle, borderBottom: 'none' }}>
            <div>
              <div style={fieldLabelStyle}>Ejendommens løbenummer</div>
              <div style={fieldHintStyle}>Boligflow har automatisk fundet det næste løbenummer.</div>
            </div>
            <div>
              <TextInput value={nextSerial} readOnly />
            </div>
          </div>
        </div>

        {/* FORBRUGSTYPER */}
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>Forbrugstyper</div>
          <div style={{ padding: '4px 24px 8px', fontSize: 12, color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>
            Vælg hvilke forbrugstyper ejendommen skal have. Du kan tilføje flere forbrugstyper senere.
          </div>
          {CONSUMPTION_TYPES.map(ct => {
            const on = form.consumption_types.has(ct.key)
            return (
              <div
                key={ct.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 24px',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <span style={{ fontSize: 12, color: '#94a3b8', width: 28, flexShrink: 0 }}>
                  {on ? 'TIL' : 'FRA'}
                </span>
                <Toggle on={on} onToggle={() => toggleConsumption(ct.key)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{ct.label}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{ct.desc}</div>
                </div>
              </div>
            )
          })}
        </div>

        {/* LEJEMÅL */}
        {(selectedHit || unitsLoading) && (
          <div style={sectionStyle}>
            <div style={{
              ...sectionHeaderStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span>Lejemål i ejendommen</span>
              {units.length > 0 && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => toggleAll(true)}
                    style={{
                      fontSize: 11, color: '#4f46e5', background: 'none',
                      border: 'none', cursor: 'pointer', textTransform: 'none',
                    }}
                  >
                    Vælg alle
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleAll(false)}
                    style={{
                      fontSize: 11, color: '#64748b', background: 'none',
                      border: 'none', cursor: 'pointer', textTransform: 'none',
                    }}
                  >
                    Fravælg alle
                  </button>
                </div>
              )}
            </div>
            {unitsLoading && (
              <div style={{ padding: '20px 24px', fontSize: 13, color: '#94a3b8' }}>
                Henter lejemål fra adresseregistret…
              </div>
            )}
            {!unitsLoading && units.length === 0 && (
              <div style={{ padding: '20px 24px', fontSize: 13, color: '#94a3b8' }}>
                Ingen lejemål fundet for denne adresse. Du kan oprette lejemål manuelt efter ejendommen er oprettet.
              </div>
            )}
            {!unitsLoading && units.length > 0 && (
              <>
                <div style={{ padding: '8px 24px', fontSize: 12, color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>
                  {units.filter(u => u.selected).length} af {units.length} lejemål valgt — de valgte oprettes automatisk under ejendommen.
                </div>
                {units.map((u, idx) => (
                  <div
                    key={u.hit.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '13px 24px',
                      borderBottom: idx < units.length - 1 ? '1px solid #f1f5f9' : 'none',
                      background: u.selected ? '#ffffff' : '#f8fafc',
                    }}
                  >
                    <Toggle on={u.selected} onToggle={() => toggleUnit(idx)} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: u.selected ? '#0f172a' : '#94a3b8',
                      }}>
                        {formatEnhed(u.hit)}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Bottom action bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          paddingTop: 8,
          paddingBottom: 32,
        }}>
          <Button type="button" variant="secondary" onClick={() => navigate('/ejendomme')}>
            Annuller
          </Button>
          <Button type="submit" isLoading={submitting} disabled={unitsLoading}>
            {unitsLoading ? 'Henter lejemål…' : 'Opret ejendom'}
          </Button>
        </div>
      </form>
    </AppLayout>
  )
}
